<template>
  <div id="content">
    <DYLoadingComponent v-if="statusCode === 401 || (asErrorObj instanceof UnauthorizedError) || error?.name === 'UnauthorizedError'">
    </DYLoadingComponent>
    <div v-else class="errorPage">
      <div class="error-code">
        {{ statusCode }}
      </div>

      <pre v-if="userStore.isCompanyIp">{{ asErrorObj ? asErrorObj.stack : error }}</pre>

      <div class="error-content mt-8">
        <div class="error-message">
          {{ message }}
          <slot name="forAdmin"></slot>
        </div>
        <div class="error-desc my-8 !text-base">
          {{ forUserMent }} <br> 일시적 장애인 경우 새로고침 하시면 해결됩니다.
        </div>

        <div class>
          <template v-if="'444' == statusCode">
            <v-btn class="px-3 text-white" color="cyan" @click="refresh">페이지 새로고침</v-btn>
          </template>
          <template v-else>
            <!--            v-if="Number(errorCode) !== 404 && Number(errorCode) !== 400"-->
            <div class="mx-auto">
              <template v-if="'502' == statusCode">
                <v-btn class="px-3 text-white" color="cyan" @click="refresh">페이지 새로고침</v-btn>
              </template>
              <v-btn v-if="'401' == statusCode && !userStore.isSignedIn" class="text-white" color="cyan" to="/login"
                append-icon="fal fa-arrow-right">
                로그인
              </v-btn>
              <v-btn v-else class="text-white" color="cyan" @click="errorReportSend" append-icon="fal fa-arrow-right">
                문제 신고하기
              </v-btn>
            </div>
            <div class="flex flex-row flex-wrap justify-center mt-4 gap-4">
              <v-btn v-if="'502' != statusCode" to="/" color="success" class="px-3">
                메인페이지로 이동
              </v-btn>
              <v-btn color="success" class="px-3" @click="historyBack">
                이전 페이지로 이동
              </v-btn>
            </div>
            <div v-if="siteName === '마이퍼스트가이드' && (userStore.isIOSApp || userStore.isAndroidApp)" class="flex flex-row flex-wrap justify-center mt-4 gap-4">
              <v-btn color="blue" class="px-3" @click.prevent="onMyContentsClick">
                다운로드함 바로가기
              </v-btn>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { library } from '@fortawesome/fontawesome-svg-core';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { notify } from '@kyvg/vue3-notification';
import { convertErrorCode } from '~/modules/nuxt3-module-doyac/composables/common';
import { useDYAppConfig } from '~/modules/nuxt3-module-doyac/composables/config';
import { $dy__fetchWithToken } from '~/modules/nuxt3-module-doyac/composables/fetch';
import { useDYErrorStore } from '~/modules/nuxt3-module-doyac/stores/error';
import { useDYUserStore } from '~/modules/nuxt3-module-doyac/stores/user';
import { UnauthorizedError } from '../composables/auth';
import { useUtils } from '../composables/utils';

library.add(faArrowRight)

const props = defineProps({
  error: Object
})

const route = useRoute()
const { fullDomain, basePath, siteName, API_BASE_URL } = useDYAppConfig()
const userStore = useDYUserStore()
const errorStore = useDYErrorStore()

/**
 * 에러 신고 시 관리자 푸시에 실리는 "이전페이지".
 *
 * 2026-08-14 [이전페이지 유실] 이 값은 선언만 되어 있고 **어디서도 대입되지 않아**, 클라이언트에서 신고하면
 * 언제나 '알 수 없음' 으로 나갔다. 원래 이 자리를 채우던 구 사이트의 `$_SESSION["pre_page"]` 는
 * `DoyacFramework::_recordPrePage()` 가 세션에 쌓는 값이라 SPA→`api.*` 경로에는 존재하지 않는다.
 * (라라벨 'api' 미들웨어 그룹에는 `StartVanillaSession` 조차 없어 `$_SESSION` 자체가 없다)
 * 게다가 cross-origin 요청이라 `HTTP_REFERER` 에도 오리진만 실리고 경로는 빠진다.
 *
 * 그래서 SPA 가 아는 직전 라우트를 여기서 직접 채운다. Vue Router 4 가 history state 에 넣어두는 값이라
 * 별도 추적이 필요 없다. `createCurrentLocation()` 이 base 를 떼고 저장하므로 basePath 를 다시 붙여야
 * REQUESTURI 와 같은 형식(전체 URL)이 된다.
 *
 * 신고 버튼을 눌렀을 때만 전송되므로 일반 요청에는 아무 영향이 없다.
 */
const referer = ref('알 수 없음')

if (import.meta.client) {
  try {
    const back = useRouter().options.history.state?.back
    if (typeof back === 'string' && back) {
      referer.value = fullDomain + basePath + back
    }
  } catch (e) {
    // 진단용 부가정보라 못 구해도 '알 수 없음' 으로 두고 넘어간다.
  }
}

const statusCode = computed(() => props.error?.statusCode ?? 500)

// if (import.meta.server) {
//   const event = useRequestEvent()
//   if (event) {
//     const code = Number(statusCode.value)
//     if (!isNaN(code)) {
//       setResponseStatus(event, code)
//     } else {
//       setResponseStatus(event, 500)
//     }
//   }
// }

const asErrorObj = computed(() => props.error instanceof Error ? props.error as Error : undefined)

const { logErrorSSR, logErrorClient } = useUtils()
if(userStore.isHuman) {
  if (import.meta.server) {
    logErrorSSR(props.error as any, route)
  } else {
    logErrorClient(props.error as any, route)
  }
}

const message = computed(() => {
  if (props.error?.data?.message) {
    return `${props.error?.data?.message}`
  }

  if (props.error?.message) {
    return `${props.error.message}`
  }

  return convertErrorCode(statusCode.value)
})

const forUserMent = computed(() => {
  if (statusCode.value === 444) { return '일시적 네트워크 에러입니다. 새로고침 후 다시 시도해주세요.' }
  if (statusCode.value !== 404 || statusCode.value !== 400) { return '이 페이지를 보고 계시다면 다른 페이지로 이동하시거나 정상적인 요청을 해 주시기 바랍니다.' }
  if (statusCode.value === 401 && !userStore.isSignedIn) { return '로그인이 만료되었습니다. 다시 로그인해주세요.' }
  return '이 페이지를 보고 계시다면 다른 페이지로 이동하시거나 아래 버튼을 눌러 신고해주세요.'
})

const handleError = () => clearError({ redirect: statusCode.value === 401 ? '/login' : '/' })

const refresh = () => {
  location.reload()
}

const historyBack = () => {
  history.back()
}

/**
 * [다운로드함 바로가기] — 앱 종류에 맞는 다리를 골라 네이티브 다운로드함을 연다.
 *
 * 🚨 **예전 코드는 앱 안에서 아무 일도 하지 않았다** (2026-09-08 중국 현장 신고).
 *   두 가지가 겹쳐 있었다:
 *
 *   ① 갈래를 `isIOS`(기기 OS)로 갈랐다. 그런데 **플러터 웹뷰 안에서는 그 값이 못 미덥다** —
 *     스토어(`stores/user.ts`)에 *"isIOS 는 여기서는 false 가 나올 수 있으므로 쓰지 않아야 함"*
 *     이라고 적혀 있다. 그래서 플러터 아이폰도 else 로 떨어졌다.
 *   ② else 는 레거시 스킴(`myfirstguideapplink://download`)으로 이동하는데, 플러터 앱은 그
 *     주소를 받지 않는다. 결국 눌러도 아무 일이 없었다.
 *
 * ▶ **같은 버그를 웹 푸터에서 이미 한 번 고쳤다**(`FlutterApp.postMessage`). 그때 이 화면은
 *   같이 옮겨지지 않고 남아 있었다. 이제 푸터·액션바와 같은 방식을 쓴다.
 *
 * 기기가 아니라 **어떤 다리가 실제로 있는지**로 고른다. 그게 판단이 어긋날 여지가 가장 적다.
 */
const onMyContentsClick = () => {
  // 플러터 앱 — 양 플랫폼 공통 다리. 가장 먼저 본다.
  if (window.FlutterApp) {
    window.FlutterApp.postMessage(JSON.stringify({ type: 'OPEN_DOWNLOAD_LIST' }))
    return
  }

  // 네이티브 안드로이드 앱
  if (window.android?.openDownloadList) {
    window.android.openDownloadList()
    return
  }

  // 네이티브 iOS 앱
  if (window.webkit?.messageHandlers?.scriptHandler) {
    window.webkit.messageHandlers.scriptHandler.postMessage({
      script: 'openDownloadManager',
      params: []
    })
    return
  }

  // 앱이 아닌데 버튼이 보였다는 뜻이라 정상 경로는 아니다. 그래도 아무 반응이 없는 것보다는 낫다.
  window.alert('앱에서만 제공되는 기능입니다.')
}

/**
 * 신고에 실을 "실제로 크래시한 페이지"의 fullPath. (2026-08-25 신설)
 *
 * 전역 핸들러가 넘긴 **바로 그 에러**일 때만 `lastGlobalErrorUrl` 을 신뢰한다.
 * 페이지별 에러(`DYPage` 를 거쳐 이 컴포넌트가 페이지 자리에 인라인으로 뜨는 경우)는 라우트가
 * 그대로 유지되므로 `route.fullPath` 가 이미 정확하고, 그때 과거 전역 에러가 남긴 값을 쓰면
 * 엉뚱한 URL 을 신고하게 된다. 그래서 동일성(===) 으로 좁힌다.
 */
const crashedFullPath = () => {
  if (errorStore.lastGlobalErrorUrl && props.error && props.error === errorStore.lastGlobalError) {
    return errorStore.lastGlobalErrorUrl
  }
  return route.fullPath
}

const errorReportSend = async (byUser: boolean = true) => {
  try {
    const params = new URLSearchParams()
    if (!byUser) {
      params.append('logOnly', 'true')
    }
    params.append('code', statusCode.value)

    /**
     * 2026-08-25 [신고 정보 오기록] 아래 세 값이 전부 사실과 다르게 나가고 있었다.
     *
     * ① REQUESTURI — `route.fullPath` 는 이 시점에 **이미 `/error` 로 바뀐 뒤**다.
     *    전역 핸들러(`DYDefault.vue` 의 `onErrorCaptured`)가 `router.replace('/error')` 로 보낸 다음에야
     *    사용자가 이 버튼을 누르기 때문이다. 그래서 운영 신고 로그의 `REQUEST_URI` 는 예외 없이
     *    `.../v/error` 였고, 정작 **어느 페이지가 깨졌는지는 어디에도 남지 않았다.**
     *    → 크래시 시점에 담아둔 `errorStore.lastGlobalErrorUrl` 을 쓴다(`crashedFullPath` 참고).
     *
     * ②③ useragent / errorType — `ssrContext` 는 **SSR 렌더 중에만** 존재한다. 그런데 이 함수는
     *    [문제 신고하기] 클릭 핸들러라 100% 브라우저에서만 실행되므로 `ssrContext` 는 언제나 `undefined`,
     *    즉 useragent 는 늘 'unknown', errorType 은 늘 'ssr' 로 굳어 있었다.
     *    브라우저에서 난 에러인데 관리자 푸시에는 "에러 시점 : ssr" 로 찍혀 SSR 장애로 오인하게 만든다.
     *    → 브라우저면 실제 UA 와 'csr' 을 보낸다.
     *
     * `headers.from`(SPA 진입 표시) 분기는 이론상의 SSR 경로라 건드리지 않고 그대로 둔다.
     */
    const { ssrContext } = useNuxtApp()
    const ssrHeaders = ssrContext?.event?.node?.req?.headers
    params.append('REQUESTURI', fullDomain + basePath + crashedFullPath())
    params.append('useragent', ssrHeaders?.['user-agent'] || (import.meta.client ? navigator.userAgent : 'unknown'))
    params.append('referer', ssrHeaders?.from ? ssrHeaders.from : referer.value)
    params.append('errorType', ssrHeaders?.from ? 'spa' : (import.meta.client ? 'csr' : 'ssr'))
    params.append('send', 'Y')
    params.append('message', message.value)
    const response = await $dy__fetchWithToken<any>(`${API_BASE_URL}/api/error/report`, { method: 'post', body: params })
    
    if (response) {
      notify({ type: 'success', text: '관리자에게 에러리포트가 전송되었습니다. 감사합니다.' })
    }
    return false
  } catch (e) {
    //
  }
}

onMounted(() => {
  if (props.error) {
    //errorReportSend(false)

    console.error(JSON.stringify(props.error))
  }
})
</script>

<style lang="scss">
.errorPage {
  margin: 0 auto;
  text-align: center;
  min-height: calc(100vh - 255px);
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center
}

.errorPage .error-code,
.errorPage .error-content {
  -webkit-box-flex: 1;
  -ms-flex: 1;
  flex: 1;
  -webkit-transform: translateZ(0);
  -moz-transform: translateZ(0);
  -ms-transform: translateZ(0);
  -o-transform: translateZ(0);
  transform: translateZ(0)
}

.errorPage .error-code {
  color: #2d353c;
  font-size: 200px;
  line-height: 1;
  text-shadow: 10px 10px 0 rgba(45, 53, 60, .2);
  padding: 1.875rem;
  height: 40vh;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -ms-flex-align: flex-end;
  align-items: flex-end
}

@media (max-width:767.98px) {
  .errorPage .error-code {
    font-size: 160px
  }
}

@media (max-width:575.98px) {
  .errorPage .error-code {
    font-size: 120px
  }
}

.errorPage .error-content {
  bottom: 0;
  padding: 1.875rem;
  background: #2d353c;
  background-image: url(https://www.doyac.com/renewal_doyac/img/background/error-page-pattern.png)
}

.errorPage .error-message {
  color: #fff;
  font-size: 1.5rem;
  line-height: 1.2;
  letter-spacing: .5px;
  margin-bottom: .46875rem
}

.errorPage .error-desc {
  font-size: .75rem;
  color: rgba(255, 255, 255, .5);
  letter-spacing: .5px
}

.btn-primary {
  color: #fff;
  background-color: #00acac;
  border-color: #00acac;
  -webkit-box-shadow: 0;
  box-shadow: 0;
}

.btn-info {
  color: #fff;
  background-color: #49b6d6;
  border-color: #49b6d6;
  -webkit-box-shadow: 0;
  box-shadow: 0;
}

.btn.btn-theme {
  padding: 12px 30px;
  font-weight: 600;
  -webkit-border-radius: 6px;
  border-radius: 6px;
}

.btn+.btn {
  margin-left: 15px;
}

.v-application .error {
  background-color: inherit !important;
  border-color: inherit !important;
}
</style>
